// ---------------------------------------------------------------------------
// C52: Public Form Viewer — no auth required, standalone shareable form
// ---------------------------------------------------------------------------
import { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router";
import { Loader2, AlertCircle } from "lucide-react";
import { suggestProgressiveProfile } from "@ease/page-builder";
import type { CrmFieldMapping, FormField } from "@/hooks/use-forms";
import { ConversationalForm } from "@/components/forms/conversational-form";
import {
  applyProgressiveProfiling,
  profilingStats,
  type ContactProfile,
} from "@/components/forms/progressive-profiler";

interface PublicFormData {
  name: string;
  fields: FormField[];
  brandColor?: string;
  thankYouMessage?: string;
}

function hasAnsweredValue(field: FormField, value: string | undefined): boolean {
  if (field.type === "checkbox") {
    return Boolean(value?.split(",").map((entry) => entry.trim()).filter(Boolean).length);
  }

  return Boolean(value?.trim());
}

function toggleCheckboxValue(currentValue: string | undefined, option: string): string {
  const selected = currentValue?.split(",").map((entry) => entry.trim()).filter(Boolean) ?? [];

  if (selected.includes(option)) {
    return selected.filter((entry) => entry !== option).join(", ");
  }

  return [...selected, option].join(", ");
}

function getProgressiveProfileVisitKey(formId: string): string {
  return `ease:forms:${formId}:visited`;
}

function getProgressiveProfileStorageKey(formId: string): string {
  return `ease:forms:${formId}:profile`;
}

function readStoredProfile(formId: string): ContactProfile | null {
  if (typeof window === "undefined") return null;

  try {
    const raw = window.localStorage.getItem(getProgressiveProfileStorageKey(formId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ContactProfile;
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

function buildKnownProfile(
  fields: FormField[],
  answers: Record<string, string>,
): ContactProfile | null {
  const profile: ContactProfile = {};

  for (const field of fields) {
    const profileField = inferProfileField(field);
    const value = answers[field.id]?.trim();

    if (!profileField || !value) {
      continue;
    }

    profile[profileField] = value;
  }

  return Object.keys(profile).length > 0 ? profile : null;
}

function inferProfileField(field: FormField): CrmFieldMapping["crmField"] | null {
  if (field.type === "email" || /email|e-mail/i.test(field.label)) return "email";
  if (field.type === "phone" || /phone|tel|mobile|cell/i.test(field.label)) return "phone";
  if (/^(first|given)\s*name$/i.test(field.label)) return "firstName";
  if (/^(last|family|sur)\s*name$/i.test(field.label)) return "lastName";
  if (/^name$|^full\s*name$/i.test(field.label)) return "firstName";
  if (/company|business|organization/i.test(field.label)) return "company";
  return null;
}

export default function PublicFormPage() {
  const { formId } = useParams();
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState<PublicFormData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [fileAnswers, setFileAnswers] = useState<Record<string, File[]>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isReturnVisitor, setIsReturnVisitor] = useState(false);
  const [knownProfile, setKnownProfile] = useState<ContactProfile | null>(null);
  const isConversationalMode = searchParams.get("mode") === "conversational";

  useEffect(() => {
    if (!formId) return;
    setIsLoading(true);
    // Public endpoint — no auth cookie needed
    fetch(`/api/forms/${formId}/public`)
      .then((res) => {
        if (!res.ok) throw new Error("Form not found");
        return res.json();
      })
      .then((data) => setForm(data.data))
      .catch((err) => setError(err.message))
      .finally(() => setIsLoading(false));
  }, [formId]);

  useEffect(() => {
    if (!formId || typeof window === "undefined") return;

    const visitKey = getProgressiveProfileVisitKey(formId);
    const hasVisitedBefore = window.localStorage.getItem(visitKey) === "1";
    setIsReturnVisitor(hasVisitedBefore);
    setKnownProfile(readStoredProfile(formId));
    window.localStorage.setItem(visitKey, "1");
  }, [formId]);

  const submitAnswers = async (submissionAnswers: Record<string, string>) => {
    if (!formId || !form) return;
    setIsSubmitting(true);
    setError(null);
    try {
      const res = await fetch(`/api/forms/${formId}/submit`, {
        method: "POST",
        ...(Object.values(fileAnswers).some((files) => files.length > 0)
          ? {
              body: (() => {
                const body = new FormData();
                body.set("answers", JSON.stringify(submissionAnswers));
                for (const [fieldId, files] of Object.entries(fileAnswers)) {
                  for (const file of files) {
                    body.append(fieldId, file);
                  }
                }
                return body;
              })(),
            }
          : {
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ answers: submissionAnswers }),
            }),
      });
      if (!res.ok) throw new Error("Submission failed");

      const nextKnownProfile = buildKnownProfile(form.fields, submissionAnswers);
      if (nextKnownProfile && typeof window !== "undefined") {
        window.localStorage.setItem(
          getProgressiveProfileStorageKey(formId),
          JSON.stringify(nextKnownProfile),
        );
        setKnownProfile(nextKnownProfile);
      }

      setIsSubmitted(true);
    } catch {
      setError("Failed to submit. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const firstMissingRequiredField = form?.fields.find((field) => {
      if (!visibleFieldIds.has(field.id) || !field.required) {
        return false;
      }

      if (field.type === "file") {
        return (fileAnswers[field.id]?.length ?? 0) === 0;
      }
      return !hasAnsweredValue(field, answers[field.id]);
    });

    if (firstMissingRequiredField) {
      setError(`Please complete ${firstMissingRequiredField.label}.`);
      return;
    }

    await submitAnswers(answers);
  };
  const inputFields = form?.fields.filter((f) => !["heading", "paragraph", "divider", "hidden"].includes(f.type)) ?? [];
  const progressiveMappings = inputFields
    .map((field) => {
      const profileField = inferProfileField(field);
      if (!profileField) {
        return null;
      }

      return {
        formFieldId: field.id,
        crmField: profileField,
      };
    })
    .filter((mapping): mapping is CrmFieldMapping => mapping !== null);
  const progressiveProfile = suggestProgressiveProfile(
    inputFields.map((field) => ({
      id: field.id,
      label: field.label,
      type: field.type,
      required: field.required,
      options: field.options,
    })),
  );
  const firstVisitFieldIds = new Set(progressiveProfile.firstVisitFields);
  const candidateFields = (isReturnVisitor || firstVisitFieldIds.size === 0
    ? inputFields
    : inputFields.filter((field) => firstVisitFieldIds.has(field.id)));
  const profiledFields = applyProgressiveProfiling(candidateFields, progressiveMappings, knownProfile);
  const profileStats = profilingStats(profiledFields);
  const visibleFieldIds = new Set(
    profiledFields
      .filter((field) => !field.hidden)
      .map((field) => field.id),
  );

  useEffect(() => {
    const prefilledAnswers = profiledFields.reduce<Record<string, string>>((next, field) => {
      if (field.prefilled && field.prefilled.trim()) {
        next[field.id] = field.prefilled;
      }
      return next;
    }, {});

    if (Object.keys(prefilledAnswers).length === 0) {
      return;
    }

    setAnswers((current) => {
      const merged = { ...prefilledAnswers, ...current };
      const changed = Object.entries(prefilledAnswers).some(
        ([fieldId, value]) => current[fieldId] !== value,
      );
      return changed ? merged : current;
    });
  }, [formId, profiledFields]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--color-background)]">
        <Loader2 className="h-8 w-8 animate-spin text-[var(--color-primary)]" />
      </div>
    );
  }

  if (error || !form) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--color-background)]">
        <div className="text-center">
          <AlertCircle className="mx-auto h-12 w-12 text-[var(--color-destructive)]" />
          <p className="mt-3 text-[var(--color-foreground)]">{error ?? "Form not found"}</p>
        </div>
      </div>
    );
  }

  if (isSubmitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--color-background)]">
        <div className="text-center max-w-md px-4">
          <div className="mx-auto h-16 w-16 rounded-full bg-[var(--color-success)]/10 flex items-center justify-center mb-4">
            <svg className="h-8 w-8 text-[var(--color-success)]" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
          </div>
          <h2 className="text-2xl font-semibold text-[var(--color-foreground)]">Thank you!</h2>
          <p className="mt-2 text-[var(--color-muted-foreground)]">{form.thankYouMessage ?? "Your response has been recorded."}</p>
        </div>
      </div>
    );
  }

  if (isConversationalMode) {
    return (
      <div className="min-h-screen bg-[var(--color-background)] py-12 px-4">
        <div className="mx-auto mb-6 max-w-lg rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
          <p className="text-sm font-medium text-[var(--color-foreground)]">
            Conversational mode
          </p>
          <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
            One question at a time. The next question can change based on previous answers and field conditions.
          </p>
        </div>
        {error ? (
          <div className="mx-auto mb-6 max-w-lg rounded-2xl border border-[var(--color-destructive)]/20 bg-[var(--color-destructive)]/5 p-4 text-sm text-[var(--color-destructive)]">
            {error}
          </div>
        ) : null}
        <ConversationalForm
          fields={form.fields}
          formName={form.name}
          onSubmit={(submissionAnswers) => {
            void submitAnswers(submissionAnswers);
          }}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--color-background)] py-12 px-4">
      <form onSubmit={handleSubmit} className="max-w-lg mx-auto">
        <h1 className="text-2xl font-bold text-[var(--color-foreground)] mb-6">{form.name}</h1>

        {progressiveProfile.returnVisitFields.length > 0 ? (
          <div className="mb-6 rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
            <p className="text-sm font-medium text-[var(--color-foreground)]">
              {profileStats.hidden > 0
                ? `Welcome back. We already know ${profileStats.hidden} detail${profileStats.hidden === 1 ? "" : "s"}, so we are only asking what is new.`
                : isReturnVisitor
                  ? "Welcome back. We are asking a few additional questions this time."
                  : "Quick first step: start with the essentials."}
            </p>
            <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
              {profileStats.hidden > 0
                ? `${profileStats.shown} field${profileStats.shown === 1 ? "" : "s"} still need${profileStats.shown === 1 ? "s" : ""} an answer because your known profile is already carrying the rest.`
                : isReturnVisitor
                ? `${progressiveProfile.returnVisitFields.length} additional field${progressiveProfile.returnVisitFields.length === 1 ? "" : "s"} unlocked for return visits.`
                : `${progressiveProfile.firstVisitFields.length} essential field${progressiveProfile.firstVisitFields.length === 1 ? "" : "s"} shown first; additional questions appear on return visits.`}
            </p>
          </div>
        ) : null}

        <div className="space-y-4">
          {form.fields.map((field) => {
            if (field.type === "heading") return <h3 key={field.id} className="text-lg font-semibold text-[var(--color-foreground)] pt-2">{field.label}</h3>;
            if (field.type === "paragraph") return <p key={field.id} className="text-sm text-[var(--color-muted-foreground)]">{field.label}</p>;
            if (field.type === "divider") return <hr key={field.id} className="border-[var(--color-border)]" />;
            if (!visibleFieldIds.has(field.id)) return null;

            const fieldControlId = `public-form-${field.id}`;

            return (
              <div key={field.id}>
                <label htmlFor={field.type === "radio" || field.type === "checkbox" ? undefined : fieldControlId} className="block text-sm font-medium text-[var(--color-foreground)] mb-1">
                  {field.label}
                  {field.required && <span className="text-[var(--color-destructive)] ml-0.5">*</span>}
                </label>
                {field.type === "textarea" ? (
                  <textarea
                    id={fieldControlId}
                    value={answers[field.id] ?? ""}
                    onChange={(e) => setAnswers((p) => ({ ...p, [field.id]: e.target.value }))}
                    required={field.required}
                    placeholder={field.placeholder}
                    rows={3}
                    className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]/20 focus:border-[var(--color-primary)] resize-none"
                  />
                ) : field.type === "file" ? (
                  <input
                    type="file"
                    required={field.required}
                    onChange={(e) => {
                      const files = Array.from(e.currentTarget.files ?? []);
                      setFileAnswers((current) => ({ ...current, [field.id]: files }));
                    }}
                    className="w-full rounded-lg border border-dashed border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)] file:mr-3 file:rounded-md file:border-0 file:bg-[var(--color-surface)] file:px-3 file:py-1.5 file:text-sm file:text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]/20"
                  />
                ) : field.type === "radio" ? (
                  <div className="space-y-2">
                    {(field.options ?? []).map((opt) => (
                      <label key={opt} className="flex items-center gap-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]">
                        <input
                          type="radio"
                          name={field.id}
                          value={opt}
                          checked={answers[field.id] === opt}
                          onChange={(e) => setAnswers((p) => ({ ...p, [field.id]: e.target.value }))}
                          className="h-4 w-4 border-[var(--color-border)]"
                        />
                        <span>{opt}</span>
                      </label>
                    ))}
                  </div>
                ) : field.type === "checkbox" ? (
                  <div className="space-y-2">
                    {(field.options ?? []).map((opt) => {
                      const selected = answers[field.id]?.split(",").map((entry) => entry.trim()).filter(Boolean).includes(opt) ?? false;
                      return (
                        <label key={opt} className="flex items-center gap-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]">
                          <input
                            type="checkbox"
                            checked={selected}
                            onChange={() => setAnswers((p) => ({
                              ...p,
                              [field.id]: toggleCheckboxValue(p[field.id], opt),
                            }))}
                            className="h-4 w-4 rounded border-[var(--color-border)]"
                          />
                          <span>{opt}</span>
                        </label>
                      );
                    })}
                  </div>
                ) : field.type === "dropdown" || field.type === "select" ? (
                  <select
                    id={fieldControlId}
                    value={answers[field.id] ?? ""}
                    onChange={(e) => setAnswers((p) => ({ ...p, [field.id]: e.target.value }))}
                    required={field.required}
                    className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]/20"
                  >
                    <option value="">{field.placeholder ?? "Select..."}</option>
                    {(field.options ?? []).map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                  </select>
                ) : (
                  <input
                    id={fieldControlId}
                    type={field.type === "email" ? "email" : field.type === "phone" ? "tel" : field.type === "number" ? "number" : field.type === "date" ? "date" : "text"}
                    value={answers[field.id] ?? ""}
                    onChange={(e) => setAnswers((p) => ({ ...p, [field.id]: e.target.value }))}
                    required={field.required}
                    placeholder={field.placeholder}
                    className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]/20 focus:border-[var(--color-primary)]"
                  />
                )}
              </div>
            );
          })}
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          className="btn-press mt-6 w-full rounded-lg py-2.5 text-sm font-medium text-white disabled:opacity-50"
          style={{ background: form.brandColor ?? "var(--gradient-energy)" }}
        >
          {isSubmitting ? "Submitting..." : "Submit"}
        </button>

        <p className="mt-4 text-center text-[10px] text-[var(--color-muted-foreground)]">
          Powered by MFM Effect
        </p>
      </form>
    </div>
  );
}
